import {
  Document,
  Paragraph,
  TextRun,
  Packer,
  BorderStyle,
  Table,
  TableRow,
  TableCell,
  WidthType,
  ShadingType,
  TabStopType,
  TabStopPosition,
} from "docx";
import type { TailoredCVContent } from "../ai/document-gen";

// ── Brand colours (hex, no #) ──────────────────────────────────────────────
const C = {
  heading:   "0F172A", // slate-950
  accent:    "4338CA", // indigo-700
  body:      "334155", // slate-700
  muted:     "64748B", // slate-500
  faint:     "94A3B8", // slate-400
  rule:      "E2E8F0", // slate-200
  tagBg:     "EEF2FF", // indigo-50
  tagText:   "3730A3", // indigo-800
  resultBg:  "F0FDF4", // emerald-50
  resultText:"166534", // emerald-800
};

// ── Size helpers (half-points) ─────────────────────────────────────────────
const pt = (n: number) => n * 2; // 11pt → 22

// ── Re-usable paragraph factories ─────────────────────────────────────────

function rule(): Paragraph {
  return new Paragraph({
    border: { bottom: { style: BorderStyle.SINGLE, size: 4, color: C.rule } },
    spacing: { before: 80, after: 120 },
    children: [],
  });
}

function sectionLabel(text: string): Paragraph {
  return new Paragraph({
    spacing: { before: 220, after: 80 },
    children: [
      new TextRun({
        text: text.toUpperCase(),
        bold: true,
        size: pt(8),
        color: C.faint,
        font: "Calibri",
        characterSpacing: 80,
      }),
    ],
  });
}

function bodyParagraph(text: string, spacingAfter = 100): Paragraph {
  return new Paragraph({
    spacing: { after: spacingAfter },
    children: [new TextRun({ text, size: pt(10.5), color: C.body, font: "Calibri" })],
  });
}

function bullet(text: string): Paragraph {
  return new Paragraph({
    bullet: { level: 0 },
    spacing: { after: 40 },
    indent: { left: 360, hanging: 180 },
    children: [new TextRun({ text, size: pt(10.5), color: C.body, font: "Calibri" })],
  });
}

// ── Competency tag table (2-column tag grid) ───────────────────────────────

function competencyTable(skills: string[]): Table {
  const half = Math.ceil(skills.length / 2);
  const left  = skills.slice(0, half);
  const right = skills.slice(half);
  const rowCount = Math.max(left.length, right.length);

  const rows = Array.from({ length: rowCount }, (_, i) =>
    new TableRow({
      children: [left[i], right[i]].map((skill) =>
        new TableCell({
          width: { size: 50, type: WidthType.PERCENTAGE },
          shading: { fill: C.tagBg, type: ShadingType.CLEAR, color: "auto" },
          margins: { top: 60, bottom: 60, left: 120, right: 120 },
          borders: {
            top:    { style: BorderStyle.NONE },
            bottom: { style: BorderStyle.NONE },
            left:   { style: BorderStyle.NONE },
            right:  { style: BorderStyle.NONE },
          },
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: skill ?? "",
                  size: pt(9.5),
                  bold: !!skill,
                  color: skill ? C.tagText : "FFFFFF",
                  font: "Calibri",
                }),
              ],
            }),
          ],
        }),
      ),
    }),
  );

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top:              { style: BorderStyle.NONE },
      bottom:           { style: BorderStyle.NONE },
      left:             { style: BorderStyle.NONE },
      right:            { style: BorderStyle.NONE },
      insideHorizontal: { style: BorderStyle.NONE },
      insideVertical:   { style: BorderStyle.NONE },
    },
    rows,
  });
}

// ── Tech stack table (category | value) ───────────────────────────────────

function techStackTable(techStack: Record<string, string>): Table {
  const entries = Object.entries(techStack);
  const rows = entries.map(
    ([category, value]) =>
      new TableRow({
        children: [
          new TableCell({
            width: { size: 28, type: WidthType.PERCENTAGE },
            borders: {
              top:    { style: BorderStyle.NONE },
              bottom: { style: BorderStyle.NONE },
              left:   { style: BorderStyle.NONE },
              right:  { style: BorderStyle.NONE },
            },
            margins: { top: 50, bottom: 50, right: 120 },
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: category,
                    bold: true,
                    size: pt(10),
                    color: C.heading,
                    font: "Calibri",
                  }),
                ],
              }),
            ],
          }),
          new TableCell({
            width: { size: 72, type: WidthType.PERCENTAGE },
            borders: {
              top:    { style: BorderStyle.NONE },
              bottom: { style: BorderStyle.NONE },
              left:   { style: BorderStyle.NONE },
              right:  { style: BorderStyle.NONE },
            },
            margins: { top: 50, bottom: 50 },
            children: [
              new Paragraph({
                children: [
                  new TextRun({ text: value, size: pt(10), color: C.body, font: "Calibri" }),
                ],
              }),
            ],
          }),
        ],
      }),
  );

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top:              { style: BorderStyle.NONE },
      bottom:           { style: BorderStyle.NONE },
      left:             { style: BorderStyle.NONE },
      right:            { style: BorderStyle.NONE },
      insideHorizontal: { style: BorderStyle.SINGLE, size: 2, color: C.rule },
      insideVertical:   { style: BorderStyle.NONE },
    },
    rows,
  });
}

// ── Role block ─────────────────────────────────────────────────────────────

function roleBlock(role: TailoredCVContent["roles"][number]): Paragraph[] {
  const paras: Paragraph[] = [];

  // Title row: "Title | Organisation" (left) + period (right) via tab
  paras.push(
    new Paragraph({
      spacing: { before: 200, after: 40 },
      tabStops: [{ type: TabStopType.RIGHT, position: TabStopPosition.MAX }],
      children: [
        new TextRun({
          text: `${role.title}  ·  ${role.organisation}`,
          bold: true,
          size: pt(11),
          color: C.heading,
          font: "Calibri",
        }),
        new TextRun({
          text: `\t${role.period}`,
          size: pt(9.5),
          color: C.muted,
          font: "Calibri",
        }),
      ],
    }),
  );

  if (role.description) {
    paras.push(
      new Paragraph({
        spacing: { after: 60 },
        children: [
          new TextRun({
            text: role.description,
            italics: true,
            size: pt(10),
            color: C.muted,
            font: "Calibri",
          }),
        ],
      }),
    );
  }

  role.bullets.forEach((b) => paras.push(bullet(b)));

  if (role.result) {
    paras.push(
      new Paragraph({
        spacing: { before: 60, after: 40 },
        shading: { fill: C.resultBg, type: ShadingType.CLEAR, color: "auto" },
        indent: { left: 120 },
        children: [
          new TextRun({
            text: "Resultaat: ",
            bold: true,
            size: pt(10),
            color: C.resultText,
            font: "Calibri",
          }),
          new TextRun({
            text: role.result,
            size: pt(10),
            color: C.resultText,
            font: "Calibri",
          }),
        ],
      }),
    );
  }

  if (role.tech) {
    paras.push(
      new Paragraph({
        spacing: { after: 80 },
        children: [
          new TextRun({
            text: `Tech: ${role.tech}`,
            italics: true,
            size: pt(9),
            color: C.faint,
            font: "Calibri",
          }),
        ],
      }),
    );
  }

  return paras;
}

// ── Main export ────────────────────────────────────────────────────────────

/**
 * Renders a TailoredCVContent object into a .docx Uint8Array.
 * Uses the Adapt MasterCV layout — professional, clean, recruiter-ready.
 */
export async function formatCV(
  content: TailoredCVContent,
  consultantName: string,
): Promise<Uint8Array> {
  const children: (Paragraph | Table)[] = [];

  // ── Header ────────────────────────────────────────────────────────────
  children.push(
    new Paragraph({
      spacing: { after: 40 },
      children: [
        new TextRun({
          text: consultantName,
          bold: true,
          size: pt(26),
          color: C.heading,
          font: "Calibri",
        }),
      ],
    }),
    new Paragraph({
      spacing: { after: 8 },
      children: [
        new TextRun({
          text: content.role_title,
          size: pt(13),
          color: C.accent,
          font: "Calibri",
        }),
      ],
    }),
    rule(),
  );

  // ── Profile ───────────────────────────────────────────────────────────
  if (content.profile_text) {
    children.push(sectionLabel("Profiel"), bodyParagraph(content.profile_text, 160));
  }

  // ── Competencies ──────────────────────────────────────────────────────
  if (content.competencies.length > 0) {
    children.push(sectionLabel("Kerncompetenties"));
    children.push(competencyTable(content.competencies));
    children.push(new Paragraph({ spacing: { after: 80 }, children: [] }));
  }

  // ── Tech stack ────────────────────────────────────────────────────────
  const techEntries = Object.keys(content.tech_stack ?? {});
  if (techEntries.length > 0) {
    children.push(sectionLabel("Technisch Profiel"));
    children.push(techStackTable(content.tech_stack));
    children.push(new Paragraph({ spacing: { after: 80 }, children: [] }));
  }

  // ── Work experience ───────────────────────────────────────────────────
  if (content.roles.length > 0) {
    children.push(sectionLabel("Werkervaring"));
    content.roles.forEach((r) => roleBlock(r).forEach((p) => children.push(p)));
  }

  // ── Education ─────────────────────────────────────────────────────────
  if (content.education.length > 0) {
    children.push(sectionLabel("Opleiding"));
    content.education.forEach((e) => {
      children.push(
        new Paragraph({
          spacing: { after: 80 },
          tabStops: [{ type: TabStopType.RIGHT, position: TabStopPosition.MAX }],
          children: [
            new TextRun({
              text: `${e.degree}  ·  ${e.institution}`,
              bold: true,
              size: pt(10.5),
              color: C.heading,
              font: "Calibri",
            }),
            new TextRun({
              text: `\t${e.year}`,
              size: pt(9.5),
              color: C.muted,
              font: "Calibri",
            }),
          ],
        }),
      );
    });
  }

  // ── Certifications ────────────────────────────────────────────────────
  if (content.certifications.length > 0) {
    children.push(sectionLabel("Certificeringen"));
    content.certifications.forEach((c) => {
      children.push(
        new Paragraph({
          spacing: { after: 60 },
          children: [
            new TextRun({ text: `• ${c}`, size: pt(10.5), color: C.body, font: "Calibri" }),
          ],
        }),
      );
    });
  }

  // ── Known gaps footer (if any) ────────────────────────────────────────
  if (content.known_gaps_not_resolved.length > 0) {
    children.push(rule());
    children.push(
      new Paragraph({
        spacing: { after: 60 },
        children: [
          new TextRun({
            text: "Noot: ",
            bold: true,
            italics: true,
            size: pt(8.5),
            color: C.muted,
            font: "Calibri",
          }),
          new TextRun({
            text: content.known_gaps_not_resolved.join(" · "),
            italics: true,
            size: pt(8.5),
            color: C.faint,
            font: "Calibri",
          }),
        ],
      }),
    );
  }

  const doc = new Document({
    styles: {
      default: {
        document: {
          run: { font: "Calibri", size: pt(10.5), color: C.body },
        },
      },
    },
    sections: [
      {
        properties: {
          page: {
            margin: { top: 720, right: 864, bottom: 720, left: 864 },
          },
        },
        children,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const arrayBuffer = await blob.arrayBuffer();
  return new Uint8Array(arrayBuffer);
}
